import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { 
  LayoutDashboard, 
  Building2, 
  TrendingUp, 
  Lightbulb, 
  AlertTriangle, 
  Award,
  Settings,
  Menu,
  X,
  LogOut,
  Search,
  BarChart3,
  GitCompare,
  Users,
  Bell
} from 'lucide-react'

const businessNavigationItems = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard
  },
  {
    name: 'Business Profile',
    href: '/business',
    icon: Building2
  },
  {
    name: 'Score History',
    href: '/score-history',
    icon: TrendingUp
  },
  {
    name: 'Recommendations',
    href: '/recommendations',
    icon: Lightbulb
  },
  {
    name: 'Alerts',
    href: '/alerts',
    icon: AlertTriangle
  },
  {
    name: 'Badges',
    href: '/badges',
    icon: Award
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: Settings
  }
]

const lenderNavigationItems = [
  {
    name: 'Lender Dashboard',
    href: '/lender/dashboard',
    icon: LayoutDashboard
  },
  {
    name: 'Business Search',
    href: '/lender/search',
    icon: Search
  },
  {
    name: 'Score Comparison',
    href: '/lender/compare',
    icon: GitCompare
  },
  {
    name: 'Portfolio',
    href: '/lender/portfolio',
    icon: BarChart3
  },
  {
    name: 'Alert Subscriptions',
    href: '/lender/alerts',
    icon: Bell
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: Settings
  }
]

export function MobileNavigation() {
  const [isOpen, setIsOpen] = useState(false)
  const { user, logout } = useAuth()
  const location = useLocation()

  const isLenderPortal = location.pathname.startsWith('/lender')
  const navigationItems = isLenderPortal ? lenderNavigationItems : businessNavigationItems

  const handleLogout = () => {
    logout()
    setIsOpen(false)
  }

  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setIsOpen(true)}
          className="p-2 rounded-md bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <Menu className="w-6 h-6" />
        </button>
      </div>

      {/* Mobile menu overlay */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu panel */}
          <div className="relative flex flex-col w-80 max-w-xs bg-white dark:bg-gray-800 shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">TL</span>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                    TrustLink
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {isLenderPortal ? 'Lender Portal' : 'Business Health'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* User info */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                  <span className="text-gray-600 dark:text-gray-300 font-medium text-sm">
                    {user?.name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {user?.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
              {navigationItems.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname === item.href
                
                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive
                        ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <Icon className="w-5 h-5 mr-3 flex-shrink-0" />
                    {item.name}
                  </NavLink>
                )
              })}
            </nav>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
              {/* Portal Switcher */}
              {user?.role === 'lender' && (
                <NavLink
                  to={isLenderPortal ? '/dashboard' : '/lender/dashboard'}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white rounded-md transition-colors"
                >
                  <Users className="w-5 h-5 mr-3" />
                  {isLenderPortal ? 'Business Portal' : 'Lender Portal'}
                </NavLink>
              )}
              
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white rounded-md transition-colors"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// Bottom navigation for mobile
export function MobileBottomNavigation() {
  const location = useLocation()
  const isLenderPortal = location.pathname.startsWith('/lender')

  const businessBottomNavItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard
    },
    {
      name: 'Alerts',
      href: '/alerts',
      icon: AlertTriangle
    },
    {
      name: 'Recommendations',
      href: '/recommendations',
      icon: Lightbulb
    },
    {
      name: 'Badges',
      href: '/badges',
      icon: Award
    }
  ]

  const lenderBottomNavItems = [
    {
      name: 'Dashboard',
      href: '/lender/dashboard',
      icon: LayoutDashboard
    },
    {
      name: 'Search',
      href: '/lender/search',
      icon: Search
    },
    {
      name: 'Compare',
      href: '/lender/compare',
      icon: GitCompare
    },
    {
      name: 'Portfolio',
      href: '/lender/portfolio',
      icon: BarChart3
    }
  ]

  const bottomNavItems = isLenderPortal ? lenderBottomNavItems : businessBottomNavItems

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-40">
      <nav className="flex">
        {bottomNavItems.map((item) => {
          const Icon = item.icon
          const isActive = location.pathname === item.href
          
          return (
            <NavLink
              key={item.name}
              to={item.href}
              className={`flex-1 flex flex-col items-center py-2 px-1 text-xs font-medium transition-colors ${
                isActive
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Icon className="w-5 h-5 mb-1" />
              <span className="truncate">{item.name}</span>
            </NavLink>
          )
        })}
      </nav>
    </div>
  )
}